import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useStoreByName } from "../../../hooks/use-stores.hooks";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Paper,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import {
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  Info,
  ArrowCircleLeftOutlined,
  Delete as DeleteIcon,
  CheckCircleOutline as SuccessIcon,
  Error as ErrorIcon,
  Business as BusinessIcon,
  Class as ClassIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { LoaderComponent } from "../../components/core/LoaderComponent";
import AdminStudentsTable from "../../components/tables/AdminStudentsTable";
import AdminStudentsTableWithActions from "../../components/tables/AdminStudentsTableWithActions";
import DeleteConfirmationModal, { DeleteType } from "../../components/modals/DeleteConfirmationModal";
import SchoolFormModal from "../../components/modals/SchoolFormModal";
import GradeFormModal from "../../components/modals/GradeFormModal";
import { deleteSchool, deleteGrade, createSchool, createGrade, getSchoolsAndGrades, getUsers, exportSchoolData, updateGradeName } from "../../../services/api/admin.service";
import { addStudentsToClass } from "../../../services/api/teacher.service";
import ExportConfirmationModal from "../../components/modals/ExportConfirmationModal";
import ImportExcelModal from "../../components/modals/ImportExcelModal";
import FailedUsersModal from "../../components/modals/FailedUsersModal";
import { ROLES, FailedUser, AddStudentsToClassRequest } from '@shared/types';
import "../../../styles/pages/class-management-page.scss";


// Types
interface AdminClass {
  id: number;
  name: string;
  students: {
    id: number;
    name: string;
    email: string;
    role_id: number;
    role_name: string;
  }[];
  teacher?: {
    id: number;
    name: string;
    email: string;
  };
  school?: {
    id: number;
    name: string;
  };
}

interface AdminSchool {
  id: number;
  name: string;
  classes: AdminClass[];
}

/**
 * מסך ניהול כיתות לאדמין - מציג את כל הכיתות במערכת
 * מאפשר לאדמין לראות נתונים על כל כיתה כמו שמורה רואה
 */
const SchoolManagementPage: React.FC = observer(() => {
  const { t } = useTranslation();
  const adminStore = useStoreByName("adminStore");
  const userStore = useStoreByName("userStore");
  const { classes, isLoading, error } = adminStore;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // בדיקת סוג המשתמש
  const userRoleId = userStore.userData?.personalInfo?.role?.id;
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isPrincipal = userRoleId === ROLES.PRINCIPAL;
  const isAdminUser = isAdmin || isPrincipal;

  // State
  const [selectedSchoolId, setSelectedSchoolId] = useState<number | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);

  // State לדיאלוג הוספת בית ספר
  const [openSchoolDialog, setOpenSchoolDialog] = useState(false);
  const [isSavingSchool, setIsSavingSchool] = useState(false);
  const [schoolFormData, setSchoolFormData] = useState({
    name: '',
    address: '',
  });

  // State לדיאלוג הוספת כיתה
  const [openGradeDialog, setOpenGradeDialog] = useState(false);
  const [isSavingGrade, setIsSavingGrade] = useState(false);
  const [gradeFormData, setGradeFormData] = useState({
    name: '',
    school_id: 0,
    teacher_id: 0,
  });

  // State למורים זמינים
  const [teachers, setTeachers] = useState<Array<{ id: number; name: string; email: string }>>([]);
  const [teacherSearchTerm, setTeacherSearchTerm] = useState('');

  // State לשרשור ייבוא תלמידים אחרי יצירת כיתה
  const [newlyCreatedGrade, setNewlyCreatedGrade] = useState<{ id: number; name: string; school_id: number } | null>(null);
  const [showAddStudentsPrompt, setShowAddStudentsPrompt] = useState(false);
  const [showImportExcelForNewGrade, setShowImportExcelForNewGrade] = useState(false);
  const [importFailedUsers, setImportFailedUsers] = useState<FailedUser[]>([]);
  const [showImportFailedUsersModal, setShowImportFailedUsersModal] = useState(false);

  // State לבתי ספר וכיתות (לשימוש במודלים)
  const [schoolsForModal, setSchoolsForModal] = useState<Array<{ id: number; name: string }>>([]);
  const [gradesForModal, setGradesForModal] = useState<
    Array<{ id: number; name: string; school_id: number; teacher?: number; School: { id: number; name: string } }>
  >([]);

  const [expandedAccordions, setExpandedAccordions] = useState<string[]>([]);
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    type: DeleteType;
    itemId: number;
    itemName: string;
    isLoading: boolean;
  }>({
    isOpen: false,
    type: 'school',
    itemId: 0,
    itemName: '',
    isLoading: false,
  });

  // State לייצוא בית ספר
  const [exportSchool, setExportSchool] = useState<AdminSchool | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // State לעריכת שם כיתה
  const [editGrade, setEditGrade] = useState<{ id: number; name: string } | null>(null);
  const [editGradeName, setEditGradeName] = useState("");
  const [isSavingEditGrade, setIsSavingEditGrade] = useState(false);

  // State להודעות
  const [resultDialog, setResultDialog] = useState({
    open: false,
    success: true,
    message: "",
  });

  // פונקציה להצגת הודעות
  const showAlert = (type: "success" | "error", message: string) => {
    setResultDialog({
      open: true,
      success: type === "success",
      message: message,
    });
  };

  // סגירת דיאלוג הודעות
  const handleCloseResultDialog = () => {
    setResultDialog({ ...resultDialog, open: false });
  };

  const handleEditGradeSubmit = async () => {
    if (!editGrade || !editGradeName.trim()) return;
    setIsSavingEditGrade(true);
    try {
      await updateGradeName(editGrade.id, editGradeName.trim());
      setEditGrade(null);
      await adminStore.loadClasses();
      showAlert("success", t("gradeNameUpdated"));
    } catch (error: any) {
      const msg = error.response?.data?.error || t("gradeNameUpdateFailed");
      showAlert("error", msg);
    } finally {
      setIsSavingEditGrade(false);
    }
  };

  const handleExportSchool = async () => {
    if (!exportSchool) return;
    setIsExporting(true);
    try {
      const blob = await exportSchoolData(exportSchool.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${exportSchool.name}_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportSchool(null);
      showAlert("success", t("exportSchoolSuccess"));
    } catch (err: any) {
      console.error("Export failed:", err);
      let errorMsg = t("exportSchoolError");
      try {
        const blob = err.response?.data;
        if (blob instanceof Blob) {
          const text = await blob.text();
          const json = JSON.parse(text);
          if (json.message || json.error) {
            errorMsg = json.message || json.error;
          }
        } else if (err.response?.data?.message || err.response?.data?.error) {
          errorMsg = err.response.data.message || err.response.data.error;
        }
      } catch { /* fallback to generic message */ }
      showAlert("error", errorMsg);
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    // טעינת נתוני כיתות
    adminStore.loadClasses();
    // טעינת בתי ספר וכיתות למודלים
    loadSchoolsAndGrades();
  }, [adminStore, adminStore.selectedSchoolId]);

  // טעינת בתי ספר וכיתות
  const loadSchoolsAndGrades = async () => {
    try {
      const data = await getSchoolsAndGrades();
      setSchoolsForModal(data.schools);
      setGradesForModal(data.grades);
    } catch (error) {
      console.error('Error loading schools and grades:', error);
      showAlert('error', t('errorLoadingSchools'));
    }
  };

  // טעינת מורים זמינים
  const loadTeachers = async () => {
    try {
      const data = await getUsers();
      // סינון רק מורים (role_id = 1) ומנהלים (role_id = 3) שיכולים לשמש כמורים
      const availableTeachers = data.users
        .filter((user: any) => user.role_id === 1 || user.role_id === 3)
        .map((user: any) => ({
          id: user.id,
          name: user.name,
          email: user.email,
        }));
      setTeachers(availableTeachers);
    } catch (error) {
      console.error('Error loading teachers:', error);
      showAlert('error', t('errorLoadingTeachers'));
    }
  };

  // פונקציות לדיאלוג בית ספר
  const handleOpenSchoolDialog = () => {
    setSchoolFormData({ name: '', address: '' });
    setOpenSchoolDialog(true);
  };

  const handleCloseSchoolDialog = () => {
    if (!isSavingSchool) {
      setOpenSchoolDialog(false);
      setSchoolFormData({ name: '', address: '' });
    }
  };

  // Helper functions for SchoolFormModal
  const handleSchoolFormChange = (field: string, value: string) => {
    setSchoolFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSchoolFormSubmit = async (formData: { name: string; address: string }) => {
    setIsSavingSchool(true);
    try {
      await createSchool({ name: formData.name.trim() });
      setSchoolFormData({ name: '', address: '' });
      setOpenSchoolDialog(false);
      await loadSchoolsAndGrades();
      await adminStore.loadClasses();
      // Show success message
      showAlert('success', t('schoolCreatedSuccessfully'));
    } catch (error: any) {
      console.error('Error creating school:', error);
      const msg = error.response?.data?.error || t('schoolCreationFailed');
      showAlert('error', msg);
    } finally {
      setIsSavingSchool(false);
    }
  };

  // פונקציות לדיאלוג כיתה
  const handleOpenGradeDialog = async () => {
    // טעינת מורים אם עדיין לא נטענו
    if (teachers.length === 0) {
      await loadTeachers();
    }
    setGradeFormData({ name: '', school_id: selectedSchoolId || 0, teacher_id: 0 });
    setOpenGradeDialog(true);
  };

  const handleCloseGradeDialog = () => {
    if (!isSavingGrade) {
      setOpenGradeDialog(false);
      setGradeFormData({ name: '', school_id: 0, teacher_id: 0 });
      setTeacherSearchTerm(''); // איפוס חיפוש המורים
    }
  };

  // Helper functions for GradeFormModal
  const handleGradeFormChange = (field: string, value: string | number) => {
    setGradeFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleGradeFormSubmit = async (formData: { name: string; school_id: number; teacher_id: number }) => {
    setIsSavingGrade(true);
    try {
      const response = await createGrade({
        name: formData.name.trim(),
        school_id: formData.school_id,
        ...(formData.teacher_id ? { teacher_id: formData.teacher_id } : {}),
      });
      setGradeFormData({ name: '', school_id: 0, teacher_id: 0 });
      setOpenGradeDialog(false);
      await loadSchoolsAndGrades();
      await adminStore.loadClasses();
      setNewlyCreatedGrade(response.grade);
      setShowAddStudentsPrompt(true);
    } catch (error: any) {
      console.error('Error creating grade:', error);
      const msg = error.response?.data?.error || t('classCreationFailed');
      showAlert('error', msg);
    } finally {
      setIsSavingGrade(false);
    }
  };

  const handleImportToNewGrade = async (file: File) => {
    if (!newlyCreatedGrade) return;
    try {
      const studentsData: AddStudentsToClassRequest = {
        classId: newlyCreatedGrade.id,
        schoolId: newlyCreatedGrade.school_id,
        file,
        name: newlyCreatedGrade.name,
      };
      const response = await addStudentsToClass(studentsData);
      if (response.details?.failed && response.details.failed.length > 0) {
        setImportFailedUsers(response.details.failed);
        setShowImportFailedUsersModal(true);
      } else {
        showAlert('success', t('studentsImportedSuccessfully'));
      }
      await loadSchoolsAndGrades();
      await adminStore.loadClasses();
    } catch (error) {
      console.error('Error importing students:', error);
      showAlert('error', t('studentsImportFailed'));
    } finally {
      setNewlyCreatedGrade(null);
      setShowImportExcelForNewGrade(false);
    }
  };

  // קבלת נתוני בתי ספר מהstore
  const schools: AdminSchool[] = classes?.schools || [];
  
  // מציאת בית ספר וכיתה נבחרים
  const selectedSchool = selectedSchoolId 
    ? schools.find(s => s.id === selectedSchoolId) 
    : null;
  const selectedClass = selectedSchool && selectedClassId
    ? selectedSchool.classes.find(c => c.id === selectedClassId)
    : null;

  // בחירת בית ספר ראשון וכיתה ראשונה כברירת מחדל
  useEffect(() => {
    if (schools.length > 0 && selectedSchoolId === null) {
      const firstSchool = schools[0];
      setSelectedSchoolId(firstSchool.id);
    }
  }, [schools, selectedSchoolId]);

  // בחירת כיתה ראשונה כברירת מחדל כשנבחר בית ספר
  useEffect(() => {
    if (selectedSchool && selectedSchool.classes.length > 0 && selectedClassId === null) {
      setSelectedClassId(selectedSchool.classes[0].id);
    }
  }, [selectedSchool, selectedClassId]);

  const handleAccordionChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedAccordions(prev => {
        if (isExpanded) {
          // הוספת הפאנל לרשימה אם הוא לא קיים
          return prev.includes(panel) ? prev : [...prev, panel];
        } else {
          // הסרת הפאנל מהרשימה
          return prev.filter(p => p !== panel);
        }
      });
    };

  // פונקציות מחיקה
  const handleDeleteSchool = (schoolId: number, schoolName: string) => {
    setDeleteModal({
      isOpen: true,
      type: 'school',
      itemId: schoolId,
      itemName: schoolName,
      isLoading: false,
    });
  };

  const handleDeleteClass = (classId: number, className: string) => {
    setDeleteModal({
      isOpen: true,
      type: 'grade',
      itemId: classId,
      itemName: className,
      isLoading: false,
    });
  };

  const handleDeleteConfirm = async () => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));

    try {
      if (deleteModal.type === 'school') {
        await deleteSchool(deleteModal.itemId);
        showAlert("success", `בית הספר "${deleteModal.itemName}" נמחק בהצלחה`);
      } else {
        await deleteGrade(deleteModal.itemId);
        showAlert("success", `הכיתה "${deleteModal.itemName}" נמחקה בהצלחה`);
      }
      
      // רענון הנתונים
      adminStore.loadClasses();
      
      // איפוס בחירה אם נמחק בית ספר/כיתה נבחר
      if (deleteModal.type === 'school' && deleteModal.itemId === selectedSchoolId) {
        setSelectedSchoolId(null);
        setSelectedClassId(null);
      } else if (deleteModal.type === 'grade' && deleteModal.itemId === selectedClassId) {
        setSelectedClassId(null);
      }
      
      // סגירת המודל
      setDeleteModal({
        isOpen: false,
        type: 'school',
        itemId: 0,
        itemName: '',
        isLoading: false,
      });
    } catch (error: any) {
      console.error('Delete error:', error);
      const errorMessage = error.response?.data?.error || t('deleteError');
      showAlert("error", errorMessage);
      
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({
      isOpen: false,
      type: 'school',
      itemId: 0,
      itemName: '',
      isLoading: false,
    });
  };

  // חישוב סטטיסטיקות
  const totalSchools = schools.length;
  const totalClasses = schools.reduce(
    (sum, school) => sum + school.classes.length,
    0
  );
  const totalStudents = schools.reduce(
    (sum, school) =>
      sum +
      school.classes.reduce(
        (classSum, cls) => classSum + cls.students.length,
        0
      ),
    0
  );

  // עדכון תלמידים אחרי הוספה/ייבוא
  const handleStudentsUpdated = async (updatedStudents: any[]) => {
    if (selectedSchool && selectedClass) {
      // רענון הנתונים מה-store
      await adminStore.loadClasses();
      // הנתונים יתעדכנו אוטומטית דרך schools
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "90vh",
          padding: { xs: 1, sm: 1.5, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px'
        }}>
          <div className="loader"></div>
          <Typography variant="h6" sx={{ 
            color: '#9c27b0', 
            fontWeight: 'bold',
            textAlign: 'center'
          }}>
            {t("loadingClasses")}...
          </Typography>
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "90vh",
          padding: { xs: 1, sm: 1.5, md: 2 },
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Paper sx={{
          p: 4,
          textAlign: 'center',
          borderRadius: '16px',
          background: 'linear-gradient(135deg, #ffebee, #ffffff)',
          border: '2px solid #f44336',
          maxWidth: '500px'
        }}>
          <Info sx={{ fontSize: 60, color: "#f44336", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1, color: "#f44336" }}>
            {t("errorLoadingData")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {error}
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "90vh",
        padding: { xs: 1, sm: 1.5, md: 2 },
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
            }}
    >
      {/* כותרת */}
      <Box sx={{ mb: 2, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: "bold", color: '#2c3e50', mb: 0.5 }}
          >
            {isPrincipal ? t("schoolManagement") : t("adminClassesManagement")} 📚
          </Typography>
          <Typography variant="body1" sx={{ color: '#7f8c8d', fontWeight: 500 }}>
            {adminStore.selectedSchoolId ? (
              <>
                {isPrincipal ? t("schoolClassesDescriptionFor") : t("classesManagementDescriptionFor")}{' '}
                <span style={{ fontWeight: 'bold', color: '#667eea' }}>
                  {adminStore.availableSchools.find((school: any) => String(school.id) === String(adminStore.selectedSchoolId))?.name || t('selectedSchool')}
                </span>
              </>
            ) : (
              <>
                {isPrincipal ? t("schoolClassesDescriptionAll") : t("classesManagementDescriptionAll")}{' '}
                <span style={{ fontWeight: 'bold', color: '#4caf50' }}>
                  {t('allSchools')}
                </span>
              </>
            )}
          </Typography>
        </Box>
        {/* כפתורי הוספה */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          {isAdmin && (
            <Button
              variant="outlined"
              color="primary"
              size={isMobile ? "small" : "medium"}
              startIcon={<BusinessIcon />}
              onClick={handleOpenSchoolDialog}
            >
              {t('addSchool')}
            </Button>
          )}
          <Button
            variant="outlined"
            color="secondary"
            size={isMobile ? "small" : "medium"}
            startIcon={<ClassIcon />}
            onClick={handleOpenGradeDialog}
            disabled={!selectedSchool}
          >
            {t('addClass')}
          </Button>
        </Box>
      </Box>

      <Card
        elevation={0}
        className="class-management-page"
        sx={{
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          width: "100%",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          flex: 1,
        }}
      >
        <CardContent 
          sx={{ 
            p: isMobile ? 1.5 : 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            flex: 1,
          }}
        >
         

                     {/* סיכום סטטיסטיקות - 3 KPIs נפרדים */}
           <Box sx={{ 
             display: 'flex',
             gap: 4,
             mb: 4,
             maxWidth: '1800px',
             width: '100%',
             margin: '0 auto 32px auto',
             flexShrink: 0,
             flexDirection: isMobile ? 'column' : 'row'
           }}>
                           {/* KPI בתי ספר */}
              <Card sx={{ 
                flex: 1,
                background: 'linear-gradient(135deg, rgba(156, 39, 176, 0.85), rgba(186, 104, 200, 0.75))',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(156, 39, 176, 0.15)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '60px',
                maxHeight: '100px',
                display: 'flex',
                alignItems: 'space-between',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(156, 39, 176, 0.2)',
                }
              }}>
                <CardContent sx={{ py: 2.5, px: 3, width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'nowrap' }}>
                 <Typography variant="h2" sx={{ fontWeight: '300', mb: 1.5, fontSize: '3rem' }}>
                   🏫
                 </Typography>
                 <Typography variant="h3" sx={{ fontWeight: '600', fontSize: '2.5rem' }}>
                   {totalSchools}
                 </Typography>
                 <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: '400', fontSize: '1.1rem' }}>
                   {t("schools")}
                 </Typography>
               </CardContent>
             </Card>

                           {/* KPI כיתות */}
              <Card sx={{ 
                flex: 1,
                background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.85), rgba(102, 187, 106, 0.75))',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(76, 175, 80, 0.15)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '60px',
                maxHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(76, 175, 80, 0.2)',
                }
              }}>
                <CardContent sx={{ py: 2.5, px: 3, width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'nowrap' }}>
                 <Typography variant="h2" sx={{ fontWeight: '300', mb: 1.5, fontSize: '3rem' }}>
                   📚
                 </Typography>
                 <Typography variant="h3" sx={{ fontWeight: '600', fontSize: '2.5rem' }}>
                   {totalClasses}
                 </Typography>
                 <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: '400', fontSize: '1.1rem' }}>
                   {t("classes")}
                 </Typography>
               </CardContent>
             </Card>

                           {/* KPI משתמשים */}
              <Card sx={{ 
                flex: 1,
                background: 'linear-gradient(135deg, rgba(88, 152, 255, 0.85), rgba(100, 181, 246, 0.75))',
                color: 'white',
                textAlign: 'center',
                boxShadow: '0 8px 32px rgba(88, 152, 255, 0.15)',
                borderRadius: 3,
                border: '1px solid rgba(255, 255, 255, 0.15)',
                minHeight: '60px',
                maxHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 40px rgba(88, 152, 255, 0.2)',
                }
              }}>
                <CardContent sx={{ py: 2.5, px: 3, width: '100%', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-around', flexWrap: 'nowrap' }}>
                 <Typography variant="h2" sx={{ fontWeight: '300', mb: 1.5, fontSize: '3rem' }}>
                   👥
                 </Typography>
                 <Typography variant="h3" sx={{ fontWeight: '600', fontSize: '2.5rem' }}>
                   {totalStudents}
                 </Typography>
                 <Typography variant="body1" sx={{ opacity: 0.95, fontWeight: '400', fontSize: '1.1rem' }}>
                   {t("users")}
                 </Typography>
               </CardContent>
             </Card>
                       </Box>

            {/* הפרדה בין KPIs לרשימת הכיתות */}
            <Box sx={{
              width: '100%',
              height: '1px',
              background: 'linear-gradient(90deg, transparent, #e0e0e0 20%, #e0e0e0 80%, transparent)',
              mb: 3,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-3px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '6px',
                background: 'linear-gradient(90deg, #9c27b0, #4caf50, #5898ff)',
                borderRadius: '3px',
                opacity: 0.6
              }
            }} />

                      {/* מבנה העמודות החדש */}
          <Box sx={{ 
            flex: 1,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'row',
            height: '100%',
          }}>
            {schools.length === 0 ? (
              <Paper sx={{ 
                p: 4, 
                textAlign: "center",
                borderRadius: 2,
                backgroundColor: '#f5f3f0',
                border: '2px solid #e1bee7',
                width: '100%'
              }}>
                <Info sx={{ fontSize: 60, color: "#9c27b0", mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 1, color: "#9c27b0" }}>
                  {t("noClassesInSystem")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {t("createFirstClassInSchool")}
                </Typography>
              </Paper>
            ) : (
              <div className="container" style={{ width: '-webkit-fill-available' }}>
                {/* עמודה ראשונה - בתי ספר (כמו עמודת פרקים) */}
                <div className="column">
                  <h5 style={{ paddingInline: '6px' }}>בתי ספר</h5>
                  <List>
                    {schools.map((school) => (
                      <ListItemButton
                        key={school.id}
                        selected={selectedSchoolId === school.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setSelectedSchoolId(school.id);
                          setSelectedClassId(null);
                        }}
                      >
                        <ListItemText 
                          primary={<p>{school.name}</p>}
                        />
                        <Tooltip title={
                          school.classes.some(c => c.students?.length > 0)
                            ? t('exportSchoolTooltip')
                            : t('noDataToExport')
                        }>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!school.classes.some(c => c.students?.length > 0)}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExportSchool(school);
                              }}
                              sx={{ 
                                color: school.classes.some(c => c.students?.length > 0) ? '#7B1FA2' : undefined,
                                '&:hover': { backgroundColor: 'rgba(123, 31, 162, 0.08)' },
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        {isAdmin && (
                          <Tooltip title={
                            school.classes.some(c => c.students?.length > 0)
                              ? t('cannotDeleteSchoolHasStudents')
                              : t('deleteSchool')
                          }>
                            <span>
                              <IconButton
                                size="small"
                                disabled={school.classes.some(c => c.students?.length > 0)}
                                aria-label={`מחק את ${school.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSchool(school.id, school.name);
                                }}
                                sx={{
                                  color: school.classes.some(c => c.students?.length > 0) ? undefined : '#d32f2f',
                                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </ListItemButton>
                    ))}
                  </List>
                </div>

                {/* עמודה שנייה - כיתות (כמו עמודת פסוקים) */}
                {selectedSchool && (
                  <div className="column">
                    <h5 style={{ paddingInline: '6px' }}>כיתות</h5>
                    {selectedSchool.classes.length === 0 ? (
                      <Box sx={{ p: 2, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                          {t("noClassesInSchool")}
                        </Typography>
                      </Box>
                    ) : (
                      <List>
                        {selectedSchool.classes.map((classItem) => (
                          <ListItemButton
                            key={classItem.id}
                            selected={selectedClassId === classItem.id}
                            onClick={() => setSelectedClassId(classItem.id)}
                          >
                            <ListItemText 
                              primary={<p>
                                {classItem.name.includes("כיתה") ? classItem.name : `כיתה ${classItem.name}`}
                              </p>}
                            />
                            <Tooltip title={t("editClassName")}>
                              <IconButton
                                size="small"
                                aria-label={`ערוך את ${classItem.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditGrade({ id: classItem.id, name: classItem.name });
                                  setEditGradeName(classItem.name);
                                }}
                                sx={{
                                  color: '#7B1FA2',
                                  '&:hover': { backgroundColor: 'rgba(123, 31, 162, 0.08)' },
                                }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("deleteClass")}>
                              <IconButton
                                size="small"
                                aria-label={`מחק את ${classItem.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteModal({
                                    isOpen: true,
                                    type: 'grade',
                                    itemId: classItem.id,
                                    itemName: classItem.name,
                                    isLoading: false,
                                  });
                                }}
                                sx={{
                                  color: '#d32f2f',
                                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.08)' },
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </ListItemButton>
                        ))}
                      </List>
                    )}
                  </div>
                )}

                {/* עמודה שלישית - תלמידים */}
                {selectedClass && selectedSchool && (
                  <div className="column" style={{ flex: 1, width: '100%' }}>
                    <AdminStudentsTableWithActions
                      students={selectedClass.students}
                      classId={selectedClass.id}
                      schoolId={selectedSchool.id}
                      className={selectedClass.name}
                      schoolName={selectedSchool.name}
                      teacherName={selectedClass.teacher?.name}
                      onStudentsUpdated={handleStudentsUpdated}
                    />
                  </div>
                )}
              </div>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Modal לאישור מחיקה */}
      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type={deleteModal.type}
        itemName={deleteModal.itemName}
        isLoading={deleteModal.isLoading}
      />

      {/* Modal לאישור ייצוא */}
      <ExportConfirmationModal
        isOpen={!!exportSchool}
        onClose={() => setExportSchool(null)}
        onConfirm={handleExportSchool}
        schoolName={exportSchool?.name || ""}
        isLoading={isExporting}
      />

      {/* דיאלוג הודעת תוצאה */}
      <Dialog
        open={resultDialog.open}
        onClose={handleCloseResultDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            padding: 2,
            textAlign: "center",
            position: 'relative',
            background: resultDialog.success
              ? "linear-gradient(135deg, #e8f5e8 0%, #f1f8f1 100%)"
              : "linear-gradient(135deg, #fdeaea 0%, #fef2f2 100%)",
            border: resultDialog.success
              ? "2px solid #4caf50"
              : "2px solid #f44336",
            boxShadow: resultDialog.success
              ? "0 20px 60px rgba(76, 175, 80, 0.3), 0 0 0 1px rgba(76, 175, 80, 0.1)"
              : "0 20px 60px rgba(244, 67, 54, 0.3), 0 0 0 1px rgba(244, 67, 54, 0.1)",
          }
        }}
      >
        <IconButton
          onClick={handleCloseResultDialog}
          className="close-button"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ py: 4, px: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
            }}
          >
            {/* אייקון מעוצב */}
            <Box
              sx={{
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "80px",
                  height: "80px",
                  borderRadius: "50%",
                  background: resultDialog.success
                    ? "rgba(76, 175, 80, 0.1)"
                    : "rgba(244, 67, 54, 0.1)",
                  animation: "pulse 2s infinite",
                },
              }}
            >
              {resultDialog.success ? (
                <SuccessIcon
                  sx={{
                    fontSize: 64,
                    color: "#4caf50",
                    filter: "drop-shadow(0 4px 8px rgba(76, 175, 80, 0.3))",
                  }}
                />
              ) : (
                <ErrorIcon
                  sx={{
                    fontSize: 64,
                    color: "#f44336",
                    filter: "drop-shadow(0 4px 8px rgba(244, 67, 54, 0.3))",
                  }}
                />
              )}
            </Box>

            {/* כותרת */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: resultDialog.success ? "#2e7d32" : "#c62828",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                letterSpacing: "0.5px",
              }}
            >
              {resultDialog.success ? t("success") : t("error")}
            </Typography>

            {/* הודעה */}
            <Box
              sx={{
                maxWidth: "400px",
                p: 2,
                bgcolor: "rgba(255,255,255,0.7)",
                borderRadius: 2,
                border: "1px solid rgba(255,255,255,0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.1rem",
                  lineHeight: 1.6,
                  color: "#333",
                  fontWeight: 500,
                }}
              >
                {resultDialog.message}
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: "center", pb: 3 }} style={{ marginBottom: 0 }}>
          <Button
            onClick={handleCloseResultDialog}
            variant="contained"
            size="large"
            sx={{
              minWidth: 120,
              py: 1.5,
              px: 4,
              borderRadius: 3,
              fontSize: "1.1rem",
              fontWeight: 600,
              textTransform: "none",
              background: resultDialog.success
                ? "linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)"
                : "linear-gradient(45deg, #f44336 30%, #ef5350 90%)",
              boxShadow: resultDialog.success
                ? "0 6px 20px rgba(76, 175, 80, 0.3)"
                : "0 6px 20px rgba(244, 67, 54, 0.3)",
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: resultDialog.success
                  ? "0 8px 25px rgba(76, 175, 80, 0.4)"
                  : "0 8px 25px rgba(244, 67, 54, 0.4)",
              },
              transition: "all 0.3s ease",
            }}
          >
            מעולה ✨
          </Button>
        </DialogActions>
      </Dialog>

      {/* School Form Modal */}
      <SchoolFormModal
        isOpen={openSchoolDialog}
        onClose={handleCloseSchoolDialog}
        onSubmit={handleSchoolFormSubmit}
        formData={schoolFormData}
        onFormChange={handleSchoolFormChange}
        isSaving={isSavingSchool}
      />

      {/* Edit Grade Name Dialog */}
      <Dialog
        open={!!editGrade}
        onClose={() => !isSavingEditGrade && setEditGrade(null)}
        maxWidth="xs"
        fullWidth
        sx={{ '& .MuiDialog-paper': { borderRadius: 3, position: 'relative' } }}
      >
        <IconButton
          onClick={() => setEditGrade(null)}
          disabled={isSavingEditGrade}
          className="close-button"
          sx={{ color: '#666' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          {t("editClassName")}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t("className")}
            value={editGradeName}
            onChange={(e) => setEditGradeName(e.target.value)}
            disabled={isSavingEditGrade}
            sx={{ mt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editGradeName.trim()) {
                handleEditGradeSubmit();
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }} style={{ marginBottom: 0 }}>
          <Button
            onClick={() => setEditGrade(null)}
            variant="outlined"
            disabled={isSavingEditGrade}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleEditGradeSubmit}
            variant="contained"
            disabled={isSavingEditGrade || !editGradeName.trim() || editGradeName.trim() === editGrade?.name}
          >
            {isSavingEditGrade ? t("saving") : t("save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grade Form Modal */}
      <GradeFormModal
        isOpen={openGradeDialog}
        onClose={handleCloseGradeDialog}
        onSubmit={handleGradeFormSubmit}
        formData={gradeFormData}
        onFormChange={handleGradeFormChange}
        schools={schoolsForModal}
        teachers={teachers}
        assignedTeacherIds={gradesForModal.filter(g => g.teacher).map(g => g.teacher as number)}
        isSaving={isSavingGrade}
      />

      {/* Prompt: add students after class creation */}
      <Dialog
        open={showAddStudentsPrompt}
        onClose={() => {
          setShowAddStudentsPrompt(false);
          setNewlyCreatedGrade(null);
          showAlert('success', t('classCreatedSuccessfully'));
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
          {t('classCreatedAddStudents')}
        </DialogTitle>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 1 }} style={{ marginBottom: 0 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setShowAddStudentsPrompt(false);
              setNewlyCreatedGrade(null);
              showAlert('success', t('classCreatedSuccessfully'));
            }}
          >
            {t('later')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setShowAddStudentsPrompt(false);
              setShowImportExcelForNewGrade(true);
            }}
          >
            {t('addStudentsNow')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Import Excel for newly created grade */}
      <ImportExcelModal
        isModalOpen={showImportExcelForNewGrade}
        setIsModalOpen={(open) => {
          setShowImportExcelForNewGrade(open);
          if (!open) setNewlyCreatedGrade(null);
        }}
        handleImportStudents={handleImportToNewGrade}
      />

      {/* Failed users from import */}
      <FailedUsersModal
        isOpen={showImportFailedUsersModal}
        onClose={() => setShowImportFailedUsersModal(false)}
        failedUsers={importFailedUsers}
      />
    </Box>
  );
});

export default SchoolManagementPage;
